import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Divider,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import { getFromLS } from "src/general_helpers/getFromLS";

import {
  deleteCuentasUsuario,
  postCuentasUsuario,
  postUser,
} from "../general_helpers/UsersData";

import { SelectorAccountsMUI } from "src/general_components/SelectorAccountsComponent";
import { SelectorProfilesMUI } from "src/general_components/SelectorProfilesComponent";

export const UserForm = (props) => {
  const { data, setCreateButton, onSuccess } = props;

  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarVisible, setSnackbarVisible] = useState(false);

  const perfiles = getFromLS("siam-perfiles")?.perfiles || [];
  const cuentas_personas_juridicas =
    getFromLS("siam-cuentas_personas_juridicas")?.cuentas_personas_juridicas ||
    [];
  const cuentas_personas_fisicas =
    getFromLS("siam-cuentas_personas_fisicas")?.cuentas_personas_fisicas || [];

  const [url_cognito, setUrlCognito] = useState("");
  const [owner_code, setOwnerCode] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const proyecto =
    JSON.parse(localStorage.getItem("parametros_proyecto") || "{}")?.id === "24";

  const userNameOrigi = data?.username || "";
  const userMailOrigi = data?.email || "";

  const defaultAccounts = useMemo(() => {
    const rel = data?.relations?.usuarios_cuentas;
    if (!rel) return [];

    if (Array.isArray(rel)) {
      return rel.map((r) => String(r.id_cuentas));
    }

    return [String(rel.id_cuentas)];
  }, [data]);

  const [form, setForm] = useState({
    id: data?.id || "",
    name: data?.nombre || "",
    surname: data?.apellido || "",
    username: data?.username || "",
    password: "",
    email: data?.email || "",
    profile: data?.id_perfiles || "",
    accounts: defaultAccounts,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const usuarioLogeado = JSON.parse(localStorage.getItem("clientData") || "{}");
    setUrlCognito(usuarioLogeado.url_cognito || "");
    setOwnerCode(usuarioLogeado.owner_code || "");
  }, []);

  useEffect(() => {
    setForm({
      id: data?.id || "",
      name: data?.nombre || "",
      surname: data?.apellido || "",
      username: data?.username || "",
      password: "",
      email: data?.email || "",
      profile: data?.id_perfiles || "",
      accounts: defaultAccounts,
    });

    setErrors({});
    setShowPassword(false);
  }, [data, defaultAccounts]);

  const validate = () => {
    const newErrors = {};

    if (!form.name) newErrors.name = "El nombre es obligatorio";
    if (!form.surname) newErrors.surname = "El apellido es obligatorio";
    if (!form.username) newErrors.username = "El username es obligatorio";

    if (!form.email) {
      newErrors.email = "El email es obligatorio";
    } else if (!form.email.includes("@")) {
      newErrors.email = "Email inválido";
    }

    if (!form.profile) newErrors.profile = "Debe seleccionar un perfil";

    if (form.password && /\s/.test(form.password)) {
      newErrors.password = "La contraseña no puede contener espacios";
    }
    // Se anula obligatoriedad de vinculacion de cuentas
    // if (proyecto) {
    //   if (!form.accounts || form.accounts.length < 1) {
    //     newErrors.accounts = "Debe seleccionar al menos una cuenta";
    //   }
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setSnackbarVisible(true);
      setMessage("Revisá los campos marcados en rojo.");
      setSeverity("warning");
      return;
    }

    try {
      let encodedPassword = "";
      if (form.password) encodedPassword = btoa(form.password);

      const userPayload = {
        id: form.id,
        nombre: form.name,
        apellido: form.surname,
        username: form.username,
        password: encodedPassword,
        email: form.email,
        id_perfiles: form.profile,
        id_cuentas: form.accounts || [],
        userNameOrigi,
        userMailOrig: userMailOrigi,
        url_cognito,
        owner_code,
      };

      const response = await postUser(userPayload);

      if (response.status === "true") {
        if (form.id) {
          await deleteCuentasUsuario(form.id);
        }

        if (userPayload.id_cuentas?.length > 0 && form.id) {
          for (const idCuenta of userPayload.id_cuentas) {
            await postCuentasUsuario(idCuenta, form.id);
          }
        }

        props.setSnackbarVisible(true);
        props.setMessage(
          form.id
            ? "Usuario modificado con éxito!!!"
            : "Usuario añadido con éxito!!!"
        );
        props.setSeverity("success");

        // ✅ REFRESCA GRILLA EN PADRE
        if (onSuccess) onSuccess();

        // ✅ CIERRA MODAL CORRECTAMENTE
        setCreateButton(false);
      } else {
        setSnackbarVisible(true);
        setMessage(
          response.message?.error_fields
            ? Object.values(response.message.error_fields).join(" | ")
            : response.message
        );
        setSeverity("warning");
      }
    } catch (err) {
      setSnackbarVisible(true);
      setMessage(err.message || "Error inesperado");
      setSeverity("error");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      autoComplete="off"
      sx={{ p: 2 }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nombre *"
            fullWidth
            value={form.name}
            onChange={handleChange("name")}
            error={!!errors.name}
            helperText={errors.name || ""}
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Apellido *"
            fullWidth
            value={form.surname}
            onChange={handleChange("surname")}
            error={!!errors.surname}
            helperText={errors.surname || ""}
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Username *"
            fullWidth
            value={form.username}
            onChange={handleChange("username")}
            error={!!errors.username}
            helperText={errors.username || ""}
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Email *"
            fullWidth
            value={form.email}
            onChange={handleChange("email")}
            error={!!errors.email}
            helperText={errors.email || ""}
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Password (opcional)"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={form.password}
            onChange={handleChange("password")}
            error={!!errors.password}
            helperText={errors.password || "Dejar vacío para mantener la actual"}
            autoComplete="new-password"
            inputProps={{
              autoComplete: "new-password",
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SelectorProfilesMUI
            perfiles={perfiles}
            value={form.profile}
            onChange={(idPerfil) => {
              setForm((prev) => ({ ...prev, profile: idPerfil }));
              setErrors((prev) => ({ ...prev, profile: undefined }));
            }}
          />
          {errors.profile && (
            <FormHelperText error>{errors.profile}</FormHelperText>
          )}
        </Grid>

        {form.profile !== "4" && (
          <Grid item xs={12}>
            <SelectorAccountsMUI
              cuentas={
                data?.id_perfiles === "4" || data?.id_perfiles === "6"
                  ? cuentas_personas_fisicas
                  : cuentas_personas_juridicas
              }
              value={form.accounts}
              onChange={(ids) => {
                setForm((prev) => ({ ...prev, accounts: ids }));
                setErrors((prev) => ({ ...prev, accounts: undefined }));
              }}
            />
            {errors.accounts && (
              <FormHelperText error>{errors.accounts}</FormHelperText>
            )}
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => setCreateButton(false)}
        >
          Cancelar
        </Button>

        <Button variant="contained" color="primary" type="submit">
          Guardar
        </Button>
      </Box>

      <SnackbarMessenger
        open={snackbarVisible}
        message={message}
        severity={severity}
        hide={8000}
        close={() => setSnackbarVisible(false)}
      />
    </Box>
  );
};
